import { useEffect, useState } from 'react'
import Modal from '../ui/Modal.jsx'
import { apiFetch } from '../../lib/api.js'

// ─────────────────────────────────────────────
// Étape 0 — Résumé des axes d'amélioration
// ─────────────────────────────────────────────
function StepSummary({ findings, onStart, onClose }) {
  const items = []
  if (findings.hasThemeIssue) items.push({ emoji: '📂', label: 'Sous-thème « Général » présent — l\'IA peut réorganiser les thèmes' })
  if (findings.duplicateGroups?.length > 0) items.push({ emoji: '♊', label: `${findings.duplicateGroups.length} groupe(s) de questions très similaires à dédoublonner` })
  if (findings.missingImages?.length > 0) items.push({ emoji: '🖼️', label: `${findings.missingImages.length} question(s) référençant une image absente` })

  return (
    <div>
      <p className="text-white/70 mb-4">L'analyse a identifié {items.length} axe(s) d'amélioration :</p>
      <ul className="space-y-2 mb-6">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-white/90">
            <span>{item.emoji}</span>
            <span>{item.label}</span>
          </li>
        ))}
      </ul>
      <div className="flex gap-3 justify-end">
        <button onClick={onClose} className="px-4 py-2 text-white/50 hover:text-white transition">
          Plus tard
        </button>
        <button onClick={onStart} className="btn-primary px-6 py-2">
          Commencer l'amélioration
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// Étape 1 — Réorganisation des sous-thèmes
// ─────────────────────────────────────────────
function StepThemes({ deckId, totalQuestions, onApplied, onSkip }) {
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState(null)
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState('')

  async function loadPreview() {
    setLoading(true)
    setError('')
    try {
      const data = await apiFetch(`/api/decks/${deckId}/improve/themes-preview`, { method: 'POST' })
      setPreview(data)
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  async function applyThemes() {
    setApplying(true)
    setError('')
    try {
      await apiFetch(`/api/decks/${deckId}/improve/themes-apply`, {
        method: 'POST',
        body: JSON.stringify(preview),
      })
      onApplied()
    } catch (e) {
      setError(e.message)
      setApplying(false)
    }
  }

  if (loading) {
    return (
      <div className="py-8 text-center">
        <div className="text-4xl mb-3 animate-pulse">🤖</div>
        <p className="text-white/60">Génération des sous-thèmes en cours…</p>
      </div>
    )
  }

  if (!preview) {
    return (
      <div>
        <p className="text-white/70 mb-6">
          Le quiz contient un sous-thème « Général ». L'IA peut analyser les {totalQuestions} questions et proposer une organisation en 6 à 9 sous-thèmes équilibrés pour le remplacer.
        </p>
        {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
        <div className="flex gap-3 justify-end">
          <button onClick={onSkip} className="px-4 py-2 text-white/50 hover:text-white transition">
            Ignorer cette étape
          </button>
          <button onClick={loadPreview} className="btn-primary px-6 py-2">
            Générer les sous-thèmes
          </button>
        </div>
      </div>
    )
  }

  const countsByST = {}
  for (const a of preview.assignments ?? []) {
    countsByST[a.sous_theme_index] = (countsByST[a.sous_theme_index] ?? 0) + 1
  }

  return (
    <div>
      <p className="text-white/70 mb-4">L'IA propose {preview.sous_themes.length} sous-thèmes :</p>
      <div className="space-y-2 mb-6 max-h-64 overflow-y-auto pr-1">
        {preview.sous_themes.map((st, i) => (
          <div key={i} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-4 py-2">
            <span className="text-white font-medium">{st.nom}</span>
            <span className="text-white/50 text-sm">{countsByST[i] ?? 0} question(s)</span>
          </div>
        ))}
      </div>
      {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
      <div className="flex gap-3 justify-end">
        <button onClick={onSkip} className="px-4 py-2 text-white/50 hover:text-white transition">
          Ignorer cette étape
        </button>
        <button onClick={() => setPreview(null)} className="px-4 py-2 text-white/70 hover:text-white transition">
          Regénérer
        </button>
        <button onClick={applyThemes} disabled={applying} className="btn-primary px-6 py-2 disabled:opacity-60">
          {applying ? 'Application…' : 'Valider et appliquer'}
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// Étape 2 — Dédoublonnage sémantique
// ─────────────────────────────────────────────
function StepDuplicates({ deckId, duplicateGroups, onApplied, onSkip }) {
  const [choices, setChoices] = useState(duplicateGroups.map(() => 0))
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState('')

  function setChoice(groupIdx, qIdx) {
    setChoices(prev => { const c = [...prev]; c[groupIdx] = qIdx; return c })
  }
  function toggleAll(groupIdx) {
    setChoices(prev => { const c = [...prev]; c[groupIdx] = c[groupIdx] === -1 ? 0 : -1; return c })
  }

  async function applyDuplicates() {
    setApplying(true)
    setError('')
    try {
      const toDelete = []
      for (let gi = 0; gi < duplicateGroups.length; gi++) {
        if (choices[gi] === -1) continue
        const group = duplicateGroups[gi]
        for (let qi = 0; qi < group.length; qi++) {
          if (qi !== choices[gi]) toDelete.push(group[qi].id)
        }
      }
      if (toDelete.length > 0) {
        await apiFetch(`/api/decks/${deckId}/improve/duplicates`, {
          method: 'DELETE',
          body: JSON.stringify({ toDelete }),
        })
      }
      onApplied(toDelete.length)
    } catch (e) {
      setError(e.message)
      setApplying(false)
    }
  }

  return (
    <div>
      <p className="text-white/70 mb-4">
        L'IA a détecté {duplicateGroups.length} groupe(s) de questions très similaires. Sélectionnez la question à conserver dans chaque groupe.
      </p>
      <div className="space-y-4 mb-6 max-h-80 overflow-y-auto pr-1">
        {duplicateGroups.map((group, gi) => (
          <div key={gi} className="border border-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/50 text-xs uppercase tracking-wide">Groupe {gi + 1}</span>
              <button
                onClick={() => toggleAll(gi)}
                className="text-xs text-white/50 hover:text-white transition"
              >
                {choices[gi] === -1 ? 'Rétablir sélection' : 'Conserver toutes'}
              </button>
            </div>
            <div className="space-y-2">
              {group.map((q, qi) => {
                const isKept = choices[gi] === -1 || choices[gi] === qi
                return (
                  <div
                    key={q.id}
                    onClick={() => choices[gi] !== -1 && setChoice(gi, qi)}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                      isKept
                        ? 'border-green-500/40 bg-green-500/10'
                        : 'border-red-500/30 bg-red-500/5 opacity-60'
                    }`}
                  >
                    <div className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                      isKept ? 'border-green-400' : 'border-red-400'
                    }`}>
                      {isKept && <div className="w-2 h-2 rounded-full bg-green-400" />}
                    </div>
                    <span className="text-sm text-white/90">{q.enonce}</span>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
      {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
      <div className="flex gap-3 justify-end">
        <button onClick={onSkip} className="px-4 py-2 text-white/50 hover:text-white transition">
          Ignorer cette étape
        </button>
        <button onClick={applyDuplicates} disabled={applying} className="btn-primary px-6 py-2 disabled:opacity-60">
          {applying ? 'Suppression…' : 'Appliquer'}
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// Étape 3 — Images manquantes
// ─────────────────────────────────────────────
function StepImages({ deckId, missingImages, onApplied, onSkip }) {
  const [actions, setActions] = useState(
    Object.fromEntries(missingImages.map(q => [q.id, 'delete_question']))
  )
  const [applying, setApplying] = useState(false)
  const [error, setError] = useState('')

  function setAction(id, action) {
    setActions(prev => ({ ...prev, [id]: action }))
  }

  async function applyImages() {
    setApplying(true)
    setError('')
    try {
      const toFix = missingImages.map(q => ({ questionId: q.id, action: actions[q.id] }))
      await apiFetch(`/api/decks/${deckId}/improve/images`, {
        method: 'POST',
        body: JSON.stringify({ toFix }),
      })
      const nbDeleted = toFix.filter(f => f.action === 'delete_question').length
      onApplied(nbDeleted)
    } catch (e) {
      setError(e.message)
      setApplying(false)
    }
  }

  return (
    <div>
      <p className="text-white/70 mb-4">
        Ces questions font référence à une image qui n'est pas fournie. Choisissez l'action pour chacune.
      </p>
      <div className="space-y-3 mb-6 max-h-72 overflow-y-auto pr-1">
        {missingImages.map(q => (
          <div key={q.id} className="border border-white/10 rounded-xl p-4">
            <p className="text-sm text-white/90 mb-3">« {q.enonce} »</p>
            <div className="flex gap-3">
              <button
                onClick={() => setAction(q.id, 'delete_question')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm border transition ${
                  actions[q.id] === 'delete_question'
                    ? 'border-red-500/60 bg-red-500/20 text-red-300'
                    : 'border-white/15 text-white/50 hover:border-white/30'
                }`}
              >
                🗑 Supprimer la question
              </button>
              <button
                onClick={() => setAction(q.id, 'ignore')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm border transition ${
                  actions[q.id] === 'ignore'
                    ? 'border-blue-500/60 bg-blue-500/20 text-blue-300'
                    : 'border-white/15 text-white/50 hover:border-white/30'
                }`}
              >
                ↩ Conserver quand même
              </button>
            </div>
          </div>
        ))}
      </div>
      {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
      <div className="flex gap-3 justify-end">
        <button onClick={onSkip} className="px-4 py-2 text-white/50 hover:text-white transition">
          Ignorer cette étape
        </button>
        <button onClick={applyImages} disabled={applying} className="btn-primary px-6 py-2 disabled:opacity-60">
          {applying ? 'Application…' : 'Appliquer'}
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────
// Étape finale — Récapitulatif
// ─────────────────────────────────────────────
function StepDone({ summary, onClose }) {
  return (
    <div className="text-center py-4">
      <div className="text-5xl mb-4">✅</div>
      <h3 className="text-white font-semibold text-lg mb-3">Amélioration terminée</h3>
      {summary.length > 0 ? (
        <ul className="text-white/70 text-sm space-y-1 mb-6">
          {summary.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      ) : (
        <p className="text-white/50 text-sm mb-6">Aucune modification appliquée.</p>
      )}
      <button onClick={onClose} className="btn-primary px-8 py-2">
        Fermer
      </button>
    </div>
  )
}

// ─────────────────────────────────────────────
// Contenu interne du modal (monté uniquement quand open=true)
// ─────────────────────────────────────────────
function ImproveDeckContent({ deckId, totalQuestions, onClose, onImproved }) {
  const [phase, setPhase] = useState('analyzing')
  const [findings, setFindings] = useState(null)
  const [analyzeError, setAnalyzeError] = useState('')
  const [summary, setSummary] = useState([])

  useEffect(() => {
    let cancelled = false
    async function runAnalyze() {
      try {
        const data = await apiFetch(`/api/decks/${deckId}/analyze`, { method: 'POST' })
        if (cancelled) return
        setFindings(data)
        const steps = buildSteps(data)
        setPhase(steps.length === 0 ? 'none' : 'summary')
      } catch (e) {
        if (cancelled) return
        setAnalyzeError(e.message)
        setPhase('error')
      }
    }
    runAnalyze()
    return () => { cancelled = true }
  }, [deckId])

  function buildSteps(f) {
    const steps = []
    if (f.hasThemeIssue) steps.push('themes')
    if (f.duplicateGroups?.length > 0) steps.push('duplicates')
    if (f.missingImages?.length > 0) steps.push('images')
    return steps
  }

  function nextPhase(current) {
    const steps = buildSteps(findings)
    const idx = steps.indexOf(current)
    return idx < steps.length - 1 ? steps[idx + 1] : 'done'
  }

  function addSummary(msg) {
    setSummary(prev => [...prev, msg])
  }

  function handleClose() {
    if (['done', 'none'].includes(phase)) onImproved?.()
    onClose()
  }

  if (phase === 'analyzing') {
    return (
      <div className="py-10 text-center">
        <div className="text-4xl mb-3 animate-pulse">🔍</div>
        <p className="text-white/60">Analyse du quiz en cours…</p>
      </div>
    )
  }

  if (phase === 'error') {
    return (
      <div>
        <p className="text-red-400 mb-4">{analyzeError}</p>
        <button onClick={onClose} className="px-4 py-2 text-white/50 hover:text-white transition">Fermer</button>
      </div>
    )
  }

  if (phase === 'none') {
    return (
      <div className="py-8 text-center">
        <div className="text-4xl mb-3">🎉</div>
        <p className="text-white/70 mb-6">Ce quiz est déjà en bonne forme, aucune amélioration nécessaire !</p>
        <button onClick={handleClose} className="btn-primary px-8 py-2">Fermer</button>
      </div>
    )
  }

  if (phase === 'summary') {
    return <StepSummary findings={findings} onStart={() => setPhase(buildSteps(findings)[0])} onClose={onClose} />
  }

  if (phase === 'themes') {
    return (
      <StepThemes
        deckId={deckId}
        totalQuestions={totalQuestions}
        onApplied={() => { addSummary('📂 Sous-thèmes réorganisés par l\'IA'); setPhase(nextPhase('themes')) }}
        onSkip={() => setPhase(nextPhase('themes'))}
      />
    )
  }

  if (phase === 'duplicates') {
    return (
      <StepDuplicates
        deckId={deckId}
        duplicateGroups={findings.duplicateGroups}
        onApplied={nb => { if (nb > 0) addSummary(`♊ ${nb} question(s) en double supprimée(s)`); setPhase(nextPhase('duplicates')) }}
        onSkip={() => setPhase(nextPhase('duplicates'))}
      />
    )
  }

  if (phase === 'images') {
    return (
      <StepImages
        deckId={deckId}
        missingImages={findings.missingImages}
        onApplied={nb => { if (nb > 0) addSummary(`🖼️ ${nb} question(s) avec image manquante supprimée(s)`); setPhase('done') }}
        onSkip={() => setPhase('done')}
      />
    )
  }

  if (phase === 'done') {
    return <StepDone summary={summary} onClose={handleClose} />
  }

  return null
}

// ─────────────────────────────────────────────
// Orchestrateur : titre dynamique + Modal
// ─────────────────────────────────────────────
export default function ImproveDeckModal({ open, deckId, totalQuestions, onClose, onImproved }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="✨ Améliorer le quiz"
      maxWidth="max-w-xl"
    >
      {/* Modal unmount le contenu quand open=false, ce qui reset l'état à chaque ouverture */}
      <ImproveDeckContent
        deckId={deckId}
        totalQuestions={totalQuestions}
        onClose={onClose}
        onImproved={onImproved}
      />
    </Modal>
  )
}
